import Link from "next/link";
import {useContext, useState} from "react";
import {useIsMobile} from "@/lib/hooks/use-mobile";
import {Input} from "@/components/shadcn/ui/input";
import {SupportForm} from "@/components/shadcn/_custom/support-form";
import {ContactForm} from "@/components/shadcn/_custom/contact-form";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/shadcn/ui/card";
import {customerServiceCategories, salesCategories, technicalCategories} from "@/data/topic-config";
import {getFormType} from "@/types/types-contact";
import {Search} from "lucide-react";
import {MobileContactAccordion} from "@/components/templates/contact/mobile-contact-accordion";
import {ContactCard} from "@/components/templates/contact/contact-card";
import {ThankYouMessage} from "@/components/templates/contact/thank-you-message";
import {DictionaryContext} from "@/context/dictionary-context";
import {Dictionary} from "@/types/hygiena-types";
import {CONTACT_PORTAL_ID} from "@/lib/constants";

export function ParagraphContactPagePortal(props) {
  return <ParagraphContactPagePortalInner {...props} />;
}

function ParagraphContactPagePortalInner({paragraph, ...props}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<{
    mainCategory: string;
    subCategory: string;
  } | null>(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState<any>(null);
  const isMobile = useIsMobile();
  const t = useContext<Dictionary>(DictionaryContext);
  // Map English logic keys to translated display labels for the selected-category header.
  const categoryDisplay: Record<string, string> = {
    "Sales Contact": t?.contact_portal?.sales_contact ?? "Sales Contact",
    "Customer Service": t?.contact_portal?.customer_service ?? "Customer Service",
    "Technical Support": t?.contact_portal?.technical_support ?? "Technical Support",
  };
  // Reverse-map topic title → topic key so we can translate the selected-topic header.
  const allTopics = [...salesCategories, ...customerServiceCategories, ...technicalCategories];
  const topicKeyByTitle = (title: string): string | undefined =>
    allTopics.find((topic) => topic.title === title)?.key;

  const handleCategorySelect = (mainCategory: string, subCategory: string) => {
    setSelectedCategory({ mainCategory, subCategory });
    // Reset form submitted state when selecting a new category
    setFormSubmitted(false);
    // Scroll to form
    setTimeout(() => {
      const formElement = document.getElementById("contact-form");
      if (formElement) {
        formElement.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const handleFormSubmit = (emailData?: any) => {
    setEmailPreviewData(emailData);
    setFormSubmitted(true);
  };

  const handleReset = () => {
    setSelectedCategory(null);
    setFormSubmitted(false);
    setEmailPreviewData(null);
  };

  const filterCategories = (categories: any[]) => {
    if (!searchQuery) return categories;
    return categories.filter(
      (category) =>
        category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        category.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Determine which form to show based on the main category
  const formType = selectedCategory
    ? getFormType(selectedCategory.mainCategory)
    : null;

  return (
    <div id={CONTACT_PORTAL_ID} className="container mx-auto px-4 pt-8 pb-16 max-w-6xl scroll-m-28">
      <div className="text-center mb-10 animate-fadeIn">
        <h1 className="text-3xl md:text-4xl font-bold text-black">
          {t?.contact_portal?.page_title ?? "Contact Us"}
        </h1>
        <p className="text-lg text-black/80">
          {t?.contact_portal?.page_intro ?? "We're here to help! Click your relevant topic or use the search below."}
        </p>
      </div>

      <div className="relative mb-8 max-w-md mx-auto animate-scaleIn">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <Input
          type="search"
          placeholder={t?.contact_portal?.search_placeholder ?? "Search for topics like pricing, warranty, ..."}
          className="pl-10 bg-white input-focus-effect"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {isMobile ? (
        <div className="space-y-4">
          <MobileContactAccordion
            title={categoryDisplay["Sales Contact"]}
            icon="handshake"
            categories={filterCategories(salesCategories)}
            onCategorySelect={(subCategory) =>
              handleCategorySelect("Sales Contact", subCategory)
            }
            color="#00AEEF"
            animationDelay="stagger-1"
          />
          <MobileContactAccordion
            title={categoryDisplay["Customer Service"]}
            icon="users"
            categories={filterCategories(customerServiceCategories)}
            onCategorySelect={(subCategory) =>
              handleCategorySelect("Customer Service", subCategory)
            }
            color="#00AEEF"
            animationDelay="stagger-3"
          />
          <MobileContactAccordion
            title={categoryDisplay["Technical Support"]}
            icon="settings"
            categories={filterCategories(technicalCategories)}
            onCategorySelect={(subCategory) =>
              handleCategorySelect("Technical Support", subCategory)
            }
            color="#00AEEF"
            animationDelay="stagger-2"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="animate-fadeInLeft stagger-1">
            <ContactCard
              title={categoryDisplay["Sales Contact"]}
              icon="handshake"
              categories={filterCategories(salesCategories)}
              onSubCategoryClick={(subCategory) =>
                handleCategorySelect("Sales Contact", subCategory)
              }
              color="#00AEEF"
              currentlySelectedSubCategory={
                selectedCategory?.mainCategory === "Sales Contact"
                  ? selectedCategory.subCategory
                  : null
              }
            />
          </div>
          <div className="animate-fadeInRight stagger-3">
            <ContactCard
              title={categoryDisplay["Customer Service"]}
              icon="users"
              categories={filterCategories(customerServiceCategories)}
              onSubCategoryClick={(subCategory) =>
                handleCategorySelect("Customer Service", subCategory)
              }
              color="#00AEEF"
              currentlySelectedSubCategory={
                selectedCategory?.mainCategory === "Customer Service"
                  ? selectedCategory.subCategory
                  : null
              }
            />
          </div>
          <div className="animate-fadeIn stagger-2">
            <ContactCard
              title={categoryDisplay["Technical Support"]}
              icon="settings"
              categories={filterCategories(technicalCategories)}
              onSubCategoryClick={(subCategory) =>
                handleCategorySelect("Technical Support", subCategory)
              }
              color="#00AEEF"
              currentlySelectedSubCategory={
                selectedCategory?.mainCategory === "Technical Support"
                  ? selectedCategory.subCategory
                  : null
              }
            />
          </div>
        </div>
      )}

      {selectedCategory && !formSubmitted && (
        <div id="contact-form" className="mt-12 animate-fadeIn">
          <Card className="mb-6 overflow-hidden">
            <CardHeader
              style={{ backgroundColor: "#00AEEF" }}
              className="text-white"
            >
              <CardTitle className="animate-fadeInLeft">
                {categoryDisplay[selectedCategory.mainCategory] ?? selectedCategory.mainCategory}: {(() => {
                  const key = topicKeyByTitle(selectedCategory.subCategory);
                  return key
                    ? t?.contact_portal?.[`topic_title_${key.replace(/-/g, "_")}`] ?? selectedCategory.subCategory
                    : selectedCategory.subCategory;
                })()}
              </CardTitle>
              <CardDescription
                className="text-white opacity-90 animate-fadeInLeft"
                style={{ animationDelay: "0.1s" }}
              >
                {t?.contact_portal?.form_intro ?? "Please provide the details below and we'll get back to you as soon as possible."}
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 pb-12">
              {formType === "support" ? (
                <SupportForm
                  onSubmitSuccess={handleFormSubmit}
                  selectedTopic={selectedCategory.subCategory}
                  selectedCategory={selectedCategory.mainCategory}
                />
              ) : (
                <ContactForm
                  onSubmitSuccess={handleFormSubmit}
                  selectedTopic={selectedCategory.subCategory}
                  selectedCategory={selectedCategory.mainCategory}
                />
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {formSubmitted && (
        <div className="mt-12 animate-scaleIn">
          <ThankYouMessage
            onReset={handleReset}
            emailPreviewData={emailPreviewData}
            formType={formType as "contact" | "support"}
          />
        </div>
      )}
    </div>
  );
}
